/**
 * „Webinar-Einladungen: vom Kontakt zur Anfo" (P3-Review, vorschlag_v3 §2.4; Till E2-A, E5-A, E10-A, E11-A).
 * Block A der Werbeflächen-Antwort, zusammen mit der früheren Quellen-Karte in EINER Tabelle: je Einladung
 * Kontakte → Anmeldungen je 1.000 → Show-up → Teilnehmer je 1.000 → erwarteter Wert je 1.000 → Abmeldungen.
 * Zähleinheit Person an ihrer ersten Anmeldung je Webinar; Anmeldungen, Show-up und Teilnehmer ohne Webinare mit
 * Tracking-Warnung (¹), der Show-up zusätzlich ohne die mit bekannter Messlücke oder übernommenen Anmeldungen (Till
 * 26.10.2026, wie P1). Unter der Tabelle der Direktvergleich Vortag/Webinartag und der Hinweis zu
 * den Webinaren ohne Teilnahmedaten. Spaltenwahl je Kartenbreite (vorschlag_v3 §5), Speicherschlüssel `einladungen`.
 *
 * Props: `data` = Antwort von /api/auswertungen/kanaele/werbeflaechen in der neuen Form (`helpers/auswertungenData.ts`,
 * `fetchWerbeflaechen`), `null` solange sie lädt; `fehler` = Ladefehler oder `null`.
 *
 * Vor dem ersten Lauf des neuen Schreibers (`personen_berechnet: false`) fehlen bei den Versand-Cache-Flächen
 * (Heat-Ups usw.) der Trichter und der Direktvergleich: dort steht „—" mit Grund, erwarteter Wert, Kontakte und
 * Abmeldungen stimmen trotzdem.
 */
import { FC, ReactNode, useMemo } from 'react';
import { Alert, Box } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { ATTRIBUTION, TERMS } from '../../../helpers/kpiDefinitions';
import { Hinweis, Laden } from '../webinare-produkte/shared';
import {
  anfosText,
  anz,
  datumLang,
  dezimal,
  euro1,
  FZeile,
  grauAnfos,
  kartenkopf,
  KontakteZelle,
  MitUnterzeile,
  NameZelle,
  prozent,
  Strich,
  summeEuro,
  tm,
  vorlaeufigHinweis,
  zeilenAus,
  zeilenInfo,
} from './flaechenTeile';
import { euroJeTausend, jeTausend, kompakt, useBreitePx } from './shared';
import type { EinladungsTrichter, FlaechenZeile, FlaechenGrauSchwellen, WerbeflaechenAntwort } from '../../../helpers/auswertungenData';

/** Grund für „—", solange der neue Schreiber noch nicht gelaufen ist (Vertrag §4). */
const NOCH_NICHT = 'Noch nicht berechnet: wird beim nächsten stündlichen Lauf nach dem Update berechnet (Personen statt Anmeldezeilen).';
const SAMMEL_GRUND = 'Ohne Kontaktzahl keine Werte je 1.000 — die Werte je Fläche stehen beim Aufklappen in den Zeilen darunter.';
const EINHEIT_E10 = 'je 1.000 Kontakte ¹';

/** Menügruppen in fester Reihenfolge (= Bedeutung, vorschlag_v3 §5.3). */
const GRUPPEN = ['Menge', 'Wert', 'Anmeldung und Teilnahme', 'Abmeldungen'] as const;

/** Standard je Kartenbreite (vorschlag_v3 §5.2): breit 7 Spalten, mittel ohne Kontakte (stehen dann im Zeilen-ⓘ), Handy 3. */
export const EINLADUNGEN_STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['name', 'kontakte', 'anm1k', 'showUp', 'teiln1k', 'wert1k', 'abm1k'],
  mittel: ['name', 'anm1k', 'showUp', 'teiln1k', 'wert1k', 'abm1k'],
  schmal: ['name', 'teiln1k', 'wert1k'],
};

/** Menü-Beschriftung = voller Begriff aus TERMS; der Tabellenkopf zeigt die Kurzform mit Einheit darunter. */
const MENUE: Record<string, string> = {
  kontakte: TERMS.kontakteZustellungen.label,
  versaende: TERMS.versaende.label,
  wert1k: TERMS.wertJe1kFlaeche.label,
  anfos1k: TERMS.anfosJe1kFlaeche.label,
  wertSumme: TERMS.wertSumme.label,
  anm1k: TERMS.anmeldungenJe1k.label,
  showUp: TERMS.showUp.label,
  teiln1k: TERMS.teilnehmerJe1k.label,
  anmAnzahl: `${TERMS.anmeldungenFlaeche.label} (Anzahl, alle Webinare)`,
  anfos100Anm: TERMS.anfosJe100AnmFlaeche.label,
  teilnAnzahl: `${TERMS.teilnehmer.label} (Anzahl)`,
  anfos100Teiln: `${TERMS.anfosJeTeilnehmer.label} (je 100)`,
  showUpIndex: TERMS.showUpIndex.label,
  abm1k: TERMS.abmeldungenJe1k.label,
  wertJeAbm: TERMS.wertJeAbmeldung.label,
  abmAnzahl: `${TERMS.abmeldungenAnzahl.label} (Anzahl)`,
};

const SCHWELLEN_ERSATZ: FlaechenGrauSchwellen = { anfos: 20, anmeldungen: 50, rechtzeitig: 50, abmeldungen: 20, nach_beginn_anmeldungen: 50 };

/** 1 → „einem Webinar", 3 → „drei Webinaren". */
const webinareText = (n: number): string => {
  const w = ['', 'einem', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn', 'elf', 'zwölf'];
  if (n === 1) return 'einem Webinar';
  return `${n < w.length ? w[n] : anz(n)} Webinaren`;
};
const starts = (l: Array<{ start: string | null }>): string =>
  l
    .map((w) => (w.start ? tm(w.start) : null))
    .filter(Boolean)
    .join(', ');

interface Kontext {
  data: WerbeflaechenAntwort | null;
  sw: FlaechenGrauSchwellen;
  sichtbar: ReadonlySet<string>;
  schmal: boolean;
  /** Mindestbreite der Namensspalte in px. */
  namensBreite: number;
  /** Trichter der beiden laufenden Heat-Ups (für das ⓘ der ganzen Liste). */
  vortag: EinladungsTrichter | null;
  webinartag: EinladungsTrichter | null;
}

/** Grund für „—" einer Trichterspalte, wenn die Zeile gar keinen Trichter hat. */
const ohneTrichterGrund = (z: FlaechenZeile): string => (z.trichter === null ? NOCH_NICHT : 'Nicht messbar.');

function baueSpalten(c: Kontext): Spalte<FZeile>[] {
  const { sw, data } = c;
  const wenig = TERMS.wenigDaten.etikett;
  const grauAnm = (t: EinladungsTrichter | null) => (t && t.anmeldungen < sw.anmeldungen ? wenig.anmeldungen : null);
  // Show-up, Teilnehmer, Index hängen an den vor Beginn Angemeldeten; das Etikett heißt dort ebenfalls „wenig Anmeldungen".
  const grauRz = (t: EinladungsTrichter | null) => (t && t.rechtzeitig < sw.rechtzeitig ? wenig.anmeldungen : null);
  const nurMitWert = (v: number | null | undefined, grund: string | null) => (v === null || v === undefined ? null : grund);
  /** Zelle einer Fläche; die Sammelzeile zeigt `sammel` oder „—" mit Grund, die Vergleichszeile `zust` (Standard „wie oben"). */
  const zelle = (
    f: (z: FlaechenZeile) => ReactNode,
    zust?: (r: Extract<FZeile, { art: 'zustellung' }>) => ReactNode,
    sammel?: (r: Extract<FZeile, { art: 'sammel' }>) => ReactNode,
  ) =>
    function zellInhalt(r: FZeile): ReactNode {
      if (r.art === 'sammel') return sammel ? sammel(r) : <Strich grund={SAMMEL_GRUND} />;
      if (r.art === 'zustellung')
        return zust ? (
          zust(r)
        ) : (
          <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
            wie oben
          </Box>
        );
      return f(r.z);
    };
  /** Sortier-/Grauwert nur für Flächen (Vergleichs- und Sammelzeile stehen fest). */
  const nurFlaeche =
    <V,>(f: (z: FlaechenZeile) => V) =>
    (r: FZeile): V | null =>
      r.art === 'flaeche' ? f(r.z) : null;
  const unsicherFlaeche = (f: (z: FlaechenZeile) => string | null) => (r: FZeile) => (r.art === 'sammel' ? null : f(r.z));

  /** „—" einer Trichterspalte: kein Trichter, Wert null mit eigenem Grund, sonst Wert. */
  const trichterZelle = (z: FlaechenZeile, f: (t: EinladungsTrichter) => ReactNode) =>
    z.trichter === null ? <Strich grund={ohneTrichterGrund(z)} /> : f(z.trichter);

  // ── ⓘ-Texte aus API-Feldern (Vertrag §3) ──
  const anmInfo = (z: FlaechenZeile, t: EinladungsTrichter): string => {
    const teile: string[] = [];
    if (t.anmeldungen_alle_webinare) {
      teile.push(
        `² Die Newsletter-Ausgaben lassen sich nicht nach Webinar trennen. Deshalb zählen hier alle Webinare; ${anz(t.ohne_teilnahmedaten.anmeldungen)} Anmeldungen liegen in Webinaren mit Tracking-Warnung. Teilnehmer je 1.000 sind deshalb nicht messbar.`,
      );
    } else if (t.ohne_teilnahmedaten.anmeldungen > 0) {
      teile.push(
        `Ohne ${anz(t.ohne_teilnahmedaten.anmeldungen)} Anmeldungen aus ${webinareText(t.ohne_teilnahmedaten.webinare.length)} mit Tracking-Warnung (${starts(t.ohne_teilnahmedaten.webinare)}); mit ihnen ${jeTausend(t.gesamt.anmeldungen_je_1000)} je 1.000.`,
      );
    }
    if (data && t.nach_beginn.anmeldungen > 0) {
      teile.push(
        `Erst nach Beginn: ${anz(t.nach_beginn.nach_beginn)} von ${anz(t.nach_beginn.anmeldungen)} Anmeldungen zu Webinaren ab ${datumLang(data.nach_beginn_erfasst_ab)} (seitdem erfasst); daraus ${anfosText(t.nach_beginn.anfos)}.`,
      );
    }
    return teile.join(' ');
  };
  const showUpInfo = (z: FlaechenZeile, t: EinladungsTrichter): string => {
    if (z.beendet_am && t.raum_anteil === 0) {
      const v = c.vortag?.aera.frueheres_werkzeug.show_up;
      const w = c.webinartag?.aera.frueheres_werkzeug.show_up;
      const vergleich =
        v !== null && v !== undefined && w !== null && w !== undefined
          ? ` Dort lagen Vortag bei ${prozent(v)} und Webinartag bei ${prozent(w)}; angezeigt wirkt die Zeile deshalb schwächer, als sie war.`
          : ' Die laufenden Heat-Ups wurden zum Teil schon im eigenen Webinarraum gemessen, der genauer misst; angezeigt wirkt die Zeile deshalb schwächer, als sie war.';
      return `Nur im früheren Werkzeug gemessen.${vergleich}`;
    }
    const teile: string[] = [];
    if (t.ohne_teilnahmedaten.rechtzeitig > 0) {
      teile.push(
        `Von ${anz(t.gesamt.rechtzeitig)} vor Beginn Angemeldeten fehlen ${anz(t.ohne_teilnahmedaten.rechtzeitig)} aus Webinaren mit Tracking-Warnung.`,
      );
    }
    const luecke = t.messluecke;
    if (luecke && luecke.rechtzeitig > 0) {
      teile.push(
        `Ebenfalls nicht im Show-up: ${anz(luecke.rechtzeitig)} vor Beginn Angemeldete aus ${webinareText(luecke.webinare.length)} mit bekannter Messlücke oder übernommenen Anmeldungen (${starts(luecke.webinare)}).`,
      );
    }
    if (t.raum_anteil !== null) {
      teile.push(`${prozent(t.raum_anteil, 0)} der übrigen wurden im eigenen Webinarraum gemessen (misst genauer als das frühere Werkzeug).`);
    }
    return teile.join(' ');
  };
  const teilnInfo = (t: EinladungsTrichter): string =>
    t.live_kaeufer_ohne_signal === null
      ? ''
      : `Untergrenze: Bei ${prozent(t.live_kaeufer_ohne_signal, 0)} der Live-Käufer dieser Zeile fehlt ein Teilnahme-Signal.`;

  const showUpZeile = (t: EinladungsTrichter) =>
    t.show_up === null ? null : `${prozent(t.show_up, 0)} Show-up`;

  return [
    {
      key: 'name',
      // Am Handy „Fläche“: Kopf „Werbefläche“+ⓘ hielt die Spalte sonst 97 px breit (3 px Überlauf bei 390 px, gemessen 23.10.).
      label: c.schmal ? TERMS.werbeflaeche.short : TERMS.werbeflaeche.label,
      tip: TERMS.werbeflaeche.tip,
      gruppe: 'Menge',
      pflicht: true,
      minWidth: c.namensBreite,
      render: (r, k) => {
        if (!data) return null;
        if (r.art === 'sammel') {
          return (
            // Name inline (der Aufklapp-Knopf der Tabelle steht davor in derselben Zeile), die Stückzahlen darunter.
            <Box component="span" sx={{ lineHeight: 1.3, fontWeight: 600 }}>
              Ohne Kontaktzahl ({anz(r.flaechen)} {r.flaechen === 1 ? 'Fläche' : 'Flächen'})
              <InfoTip text="Flächen ohne Empfängerzahl: Ihre Anmeldungen und Anfos zählen, Werte je 1.000 Kontakte gibt es nicht. Show-up und Anzahlen stehen in den Zeilen darunter." />
              <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
                {anz(r.anmeldungen)} Anmeldungen · {anfosText(r.anfos)}
              </Box>
            </Box>
          );
        }
        if (r.art === 'zustellung') {
          return (
            <Box component="span" sx={{ display: 'block', lineHeight: 1.3 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                ↳ dieselben Versände je zugestellter Mail
              </Box>
              <InfoTip
                text={`Oben zählen die Anmeldungen der Inaktiven mit, ihre Mails aber nicht (Kontakte = nur aktive). Diese Zeile teilt durch alle ${anz(r.je.kontakte)} Zustellungen und rechnet damit wie die laufenden Heat-Ups, die nur an Aktive gehen. Show-up und Anzahlen wie oben.`}
              />
              <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
                so mit den laufenden Heat-Ups vergleichen
              </Box>
            </Box>
          );
        }
        const z = r.z;
        const t = z.trichter;
        const kontakteVersteckt = !c.sichtbar.has('kontakte') && z.kontakte !== null;
        const info = zeilenInfo(z, [
          kontakteVersteckt && `${anz(z.kontakte)} Kontakte (zugestellte Mails) in ${anz(z.versaende)} ${z.versaende === 1 ? 'Versand' : 'Versänden'}.`,
          z.kontakte_alle !== null && `Alle Zustellungen: ${anz(z.kontakte_alle)}.`,
          z.provisional > 0 && `Dazu ${anz(z.provisional)} vorläufig ergänzte Anfos, die in keiner Quote zählen.`,
        ]);
        const unter =
          z.kontakte === null ? `${anz(t?.gesamt.anmeldungen ?? z.anmeldungen_live)} Anmeldungen · ${anfosText(z.anfos_ohne_vorlaeufige)}` : undefined;
        return <NameZelle z={z} data={data} info={info} sortGrund={k.ebene === 0 ? k.sortGrund : null} unter={unter} />;
      },
    },
    {
      key: 'kontakte',
      label: TERMS.kontakteZustellungen.label,
      tip: TERMS.kontakteZustellungen.tip,
      hinweis: 'Darunter die Zahl der Versände; der genaue Wert steht im Tooltip der Zahl.',
      einheit: 'zugestellte Mails',
      align: 'right',
      gruppe: 'Menge',
      render: zelle(
        (z) => <KontakteZelle z={z} mitVersaenden={!c.sichtbar.has('versaende')} />,
        (r) => <MitUnterzeile wert={kompakt(r.je.kontakte)} unter="alle Zustellungen" />,
      ),
      sortValue: nurFlaeche((z) => z.kontakte),
    },
    {
      key: 'versaende',
      label: TERMS.versaende.label,
      tip: TERMS.versaende.tip,
      einheit: 'Anzahl',
      align: 'right',
      gruppe: 'Menge',
      render: zelle((z) => anz(z.versaende)),
      sortValue: nurFlaeche((z) => z.versaende),
    },
    {
      key: 'anm1k',
      label: TERMS.anmeldungenFlaeche.label,
      tip: TERMS.anmeldungenJe1k.tip,
      hinweis: `Darunter der Anteil erst nach Beginn Angemeldeter, sobald ${anz(sw.nach_beginn_anmeldungen)} Anmeldungen zu Webinaren ab ${data ? datumLang(data.nach_beginn_erfasst_ab) : '25.08.2026'} vorliegen (sonst nur im ⓘ). Grau unter ${anz(sw.anmeldungen)} Anmeldungen. ¹ Ohne Webinare mit Tracking-Warnung.`,
      einheit: EINHEIT_E10,
      align: 'right',
      gruppe: 'Anmeldung und Teilnahme',
      render: zelle(
        (z) =>
          trichterZelle(z, (t) => {
            const info = anmInfo(z, t);
            const nb = t.nach_beginn;
            const unter =
              t.anmeldungen_je_1000 !== null && nb.anmeldungen >= sw.nach_beginn_anmeldungen && nb.anteil !== null ? `${prozent(nb.anteil, 0)} erst nach Beginn` : undefined;
            const wert =
              t.anmeldungen_je_1000 === null ? (
                <Strich grund={z.ohne_nenner_grund ?? t.teilnehmer_je_1000_grund ?? 'Nicht messbar: keine Kontakte zu Webinaren mit Teilnahmedaten.'} />
              ) : (
                <>
                  {jeTausend(t.anmeldungen_je_1000)}
                  {info && <InfoTip text={info} />}
                  {t.anmeldungen_alle_webinare && <sup>²</sup>}
                </>
              );
            return <MitUnterzeile wert={wert} unter={unter} />;
          }),
        (r) =>
          r.je.anmeldungen_je_1000 === null ? (
            <Strich grund={r.z.trichter === null ? NOCH_NICHT : 'Nicht messbar.'} />
          ) : (
            jeTausend(r.je.anmeldungen_je_1000)
          ),
      ),
      sortValue: nurFlaeche((z) => z.trichter?.anmeldungen_je_1000 ?? null),
      unsicher: unsicherFlaeche((z) => nurMitWert(z.trichter?.anmeldungen_je_1000, grauAnm(z.trichter))),
    },
    {
      key: 'anmAnzahl',
      label: TERMS.anmeldungenFlaeche.label,
      tip: TERMS.anmeldungenFlaeche.tip,
      hinweis: 'Alle Webinare, auch die mit Tracking-Warnung. Darunter: erst nach Beginn Angemeldete (nicht in der Zahl).',
      einheit: 'Anzahl, alle Webinare',
      align: 'right',
      gruppe: 'Anmeldung und Teilnahme',
      render: zelle((z) => trichterZelle(z, (t) => <MitUnterzeile wert={anz(t.gesamt.anmeldungen)} unter={`${anz(t.gesamt.nach_beginn)} nach Beginn`} />)),
      sortValue: nurFlaeche((z) => z.trichter?.gesamt.anmeldungen ?? null),
    },
    {
      key: 'showUp',
      label: TERMS.showUp.short,
      tip: TERMS.showUp.tip,
      hinweis: `Darunter: live dabei von vor Beginn Angemeldeten. Grau unter ${anz(sw.rechtzeitig)} vor Beginn Angemeldeten. ¹ Ohne Webinare mit Tracking-Warnung und ohne die mit bekannter Messlücke oder übernommenen Anmeldungen (wie „Webinare & Produkte“).`,
      einheit: 'der vor Beginn Angemeldeten ¹',
      align: 'right',
      gruppe: 'Anmeldung und Teilnahme',
      render: zelle((z) =>
        trichterZelle(z, (t) => {
          if (t.show_up === null) return <Strich grund="Keine vor Beginn Angemeldeten in Webinaren ohne Tracking-Warnung, Messlücke oder übernommene Anmeldungen." />;
          const info = showUpInfo(z, t);
          return (
            <MitUnterzeile
              wert={
                <>
                  {/* Aus den Zählern (exakt wie „x von y“); die API rundet den Bruch auf vier Stellen. */}
                  {prozent(t.rechtzeitig > 0 ? t.teilnehmer_rechtzeitig / t.rechtzeitig : t.show_up)}
                  {info && <InfoTip text={info} />}
                </>
              }
              unter={`${anz(t.teilnehmer_rechtzeitig)} von ${anz(t.rechtzeitig)}`}
            />
          );
        }),
      ),
      sortValue: nurFlaeche((z) => z.trichter?.show_up ?? null),
      unsicher: unsicherFlaeche((z) => nurMitWert(z.trichter?.show_up, grauRz(z.trichter))),
    },
    {
      key: 'teiln1k',
      // Am Handy die Kurzform (Menü und Tooltip tragen den vollen Begriff): sonst 4 px Überlauf bei 390 px (gemessen 23.10.).
      label: c.schmal ? TERMS.teilnehmer.short : TERMS.teilnehmer.label,
      tip: TERMS.teilnehmerJe1k.tip,
      hinweis: `${c.sichtbar.has('showUp') ? '' : 'Darunter der Show-up. '}Grau unter ${anz(sw.rechtzeitig)} vor Beginn Angemeldeten. ¹ Ohne Webinare mit Tracking-Warnung; die mit bekannter Messlücke oder übernommenen Anmeldungen zählen mit (bei einer Messlücke sind ihre Teilnehmer zu niedrig erfasst).`,
      einheit: EINHEIT_E10,
      align: 'right',
      gruppe: 'Anmeldung und Teilnahme',
      render: zelle(
        (z) =>
          trichterZelle(z, (t) => {
            // Handy-Standard: Show-up unter „Teilnehmer" (einzige Ausnahme der Zwei-Zeilen-Regel, vorschlag_v3 §5.7).
            const unter = c.sichtbar.has('showUp') ? undefined : showUpZeile(t) ?? undefined;
            if (t.teilnehmer_je_1000 === null) {
              return <MitUnterzeile wert={<Strich grund={t.teilnehmer_je_1000_grund ?? z.ohne_nenner_grund ?? 'Nicht messbar.'} />} unter={unter} />;
            }
            const info = teilnInfo(t);
            return (
              <MitUnterzeile
                wert={
                  <>
                    {jeTausend(t.teilnehmer_je_1000)}
                    {info && <InfoTip text={info} />}
                  </>
                }
                unter={unter}
              />
            );
          }),
        (r) =>
          r.je.teilnehmer_je_1000 === null ? (
            <Strich grund={r.z.trichter === null ? NOCH_NICHT : 'Nicht messbar.'} />
          ) : (
            jeTausend(r.je.teilnehmer_je_1000)
          ),
      ),
      sortValue: nurFlaeche((z) => z.trichter?.teilnehmer_je_1000 ?? null),
      unsicher: unsicherFlaeche((z) => nurMitWert(z.trichter?.teilnehmer_je_1000, grauRz(z.trichter))),
    },
    {
      key: 'teilnAnzahl',
      label: TERMS.teilnehmer.label,
      tip: TERMS.teilnehmer.tip,
      hinweis: 'Einschließlich Späteinlass; ohne Webinare mit Tracking-Warnung.',
      einheit: 'Anzahl, mit Späteinlass',
      align: 'right',
      gruppe: 'Anmeldung und Teilnahme',
      render: zelle((z) => trichterZelle(z, (t) => anz(t.teilnehmer))),
      sortValue: nurFlaeche((z) => z.trichter?.teilnehmer ?? null),
    },
    {
      key: 'showUpIndex',
      label: TERMS.showUpIndex.label,
      tip: TERMS.showUpIndex.tip,
      hinweis: `Grau unter ${anz(sw.rechtzeitig)} vor Beginn Angemeldeten.`,
      einheit: '1,00 = Schnitt',
      align: 'right',
      gruppe: 'Anmeldung und Teilnahme',
      render: zelle((z) =>
        trichterZelle(z, (t) => (t.show_up_index === null ? <Strich grund="Keine vor Beginn Angemeldeten in Webinaren mit Teilnahmedaten." /> : dezimal(t.show_up_index, 2))),
      ),
      sortValue: nurFlaeche((z) => z.trichter?.show_up_index ?? null),
      unsicher: unsicherFlaeche((z) => nurMitWert(z.trichter?.show_up_index, grauRz(z.trichter))),
    },
    {
      key: 'anfos100Anm',
      label: 'Anfos',
      tip: TERMS.anfosJe100AnmFlaeche.tip,
      hinweis: `Darunter: Anfos von Anmeldungen. Ohne vorläufig ergänzte Anfos. Grau unter ${anz(sw.anfos)} Anfos.`,
      einheit: 'je 100 Anmeldungen',
      align: 'right',
      gruppe: 'Anmeldung und Teilnahme',
      render: zelle((z) =>
        trichterZelle(z, (t) =>
          t.gesamt.anfos_je_100_anmeldungen === null ? (
            <Strich grund="Keine Live-Anmeldungen." />
          ) : (
            <MitUnterzeile wert={dezimal(t.gesamt.anfos_je_100_anmeldungen, 1)} unter={`${anz(t.gesamt.anfos_live)} von ${anz(t.gesamt.anmeldungen)}`} />
          ),
        ),
      ),
      sortValue: nurFlaeche((z) => z.trichter?.gesamt.anfos_je_100_anmeldungen ?? null),
      unsicher: unsicherFlaeche((z) =>
        nurMitWert(z.trichter?.gesamt.anfos_je_100_anmeldungen, z.trichter ? grauAnfos(z.trichter.gesamt.anfos_live, sw, wenig.anfos) : null),
      ),
    },
    {
      key: 'anfos100Teiln',
      label: 'Anfos',
      tip: TERMS.anfosJeTeilnehmer.tip,
      hinweis: `Anfos der live angemeldeten Teilnehmer dieser Fläche (erste Anmeldung), ohne vorläufig ergänzte; ohne Webinare mit Tracking-Warnung. Darunter: Anfos von Teilnehmern. Grau unter ${anz(sw.anfos)} Anfos.`,
      einheit: 'je 100 Teilnehmer',
      align: 'right',
      gruppe: 'Anmeldung und Teilnahme',
      render: zelle((z) =>
        trichterZelle(z, (t) =>
          t.anfos_je_100_teilnehmer === null ? (
            <Strich grund="Keine Teilnehmer in Webinaren mit Teilnahmedaten." />
          ) : (
            <MitUnterzeile wert={dezimal(t.anfos_je_100_teilnehmer, 1)} unter={`${anz(t.anfos_teilnehmer)} von ${anz(t.teilnehmer)}`} />
          ),
        ),
      ),
      sortValue: nurFlaeche((z) => z.trichter?.anfos_je_100_teilnehmer ?? null),
      unsicher: unsicherFlaeche((z) =>
        nurMitWert(z.trichter?.anfos_je_100_teilnehmer, z.trichter ? grauAnfos(z.trichter.anfos_teilnehmer, sw, wenig.anfos) : null),
      ),
    },
    {
      key: 'wert1k',
      label: TERMS.wertBeiBestellung.short,
      tip: TERMS.wertJe1kFlaeche.tip,
      hinweis: `Darunter die Anfos (ohne vorläufig ergänzte). Grau unter ${anz(sw.anfos)} Anfos. Alle Webinare.`,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      gruppe: 'Wert',
      pflicht: true,
      render: zelle(
        (z) => {
          if (z.wert_je_1000 === null) {
            return (
              <Strich
                grund={z.ohne_nenner_grund ?? (z.anfos_ohne_wert > 0 ? 'Keine Anfo dieser Fläche hat einen erwarteten Wert.' : 'Nicht messbar.')}
              />
            );
          }
          const tip = [
            z.anfos_ohne_wert > 0 ? `${anz(z.anfos_ohne_wert)} Anfos ohne Wert zählen 0 €.` : '',
            z.provisional > 0 ? `+${anz(z.provisional)} vorläufig ergänzt (nicht im Wert).` : '',
            // Fester Text nach vorschlag_v3 §2.4, nur am Webinartag (Datum aus der API).
            z.key === 'heatup_webinartag' && data
              ? `Vor dem ${tm(data.nach_beginn_erfasst_ab)} wurden Anmeldungen nach Beginn nicht gespeichert; Käufe dieser Personen fehlen hier. Der Wert ist deshalb eher zu niedrig.`
              : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <MitUnterzeile
              fett
              wert={
                <>
                  {euroJeTausend(z.wert_je_1000)}
                  {tip && <InfoTip text={tip} />}
                </>
              }
              unter={anfosText(z.anfos_ohne_vorlaeufige)}
            />
          );
        },
        (r) => (r.je.wert_je_1000 === null ? <Strich grund="Nicht messbar." /> : euroJeTausend(r.je.wert_je_1000)),
      ),
      sortValue: nurFlaeche((z) => z.wert_je_1000),
      unsicher: unsicherFlaeche((z) => nurMitWert(z.wert_je_1000, grauAnfos(z.anfos_ohne_vorlaeufige, sw, wenig.anfos))),
    },
    {
      key: 'anfos1k',
      label: TERMS.anfos.label,
      tip: TERMS.anfosJe1kFlaeche.tip,
      hinweis: `Darunter die Anfos (ohne vorläufig ergänzte). Grau unter ${anz(sw.anfos)} Anfos.`,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      gruppe: 'Wert',
      render: zelle(
        (z) =>
          z.anfos_je_1000 === null ? (
            <Strich grund={z.ohne_nenner_grund ?? 'Nicht messbar.'} />
          ) : (
            <MitUnterzeile wert={jeTausend(z.anfos_je_1000)} unter={anfosText(z.anfos_ohne_vorlaeufige)} />
          ),
        (r) => (r.je.anfos_je_1000 === null ? <Strich grund="Nicht messbar." /> : jeTausend(r.je.anfos_je_1000)),
      ),
      sortValue: nurFlaeche((z) => z.anfos_je_1000),
      unsicher: unsicherFlaeche((z) => nurMitWert(z.anfos_je_1000, grauAnfos(z.anfos_ohne_vorlaeufige, sw, wenig.anfos))),
    },
    {
      key: 'wertSumme',
      label: TERMS.wertBeiBestellung.short,
      tip: TERMS.wertSumme.tip,
      einheit: 'Summe',
      align: 'right',
      gruppe: 'Wert',
      // Summen brauchen keine Kontaktzahl — die Sammelzeile zeigt ihre (wie in den Bestell-Links).
      render: zelle((z) => summeEuro(z.wert_summe), undefined, (r) => summeEuro(r.wertSumme)),
      sortValue: nurFlaeche((z) => z.wert_summe),
    },
    {
      key: 'abm1k',
      label: c.schmal ? TERMS.abmeldungenAnzahl.short : TERMS.abmeldungenAnzahl.label,
      tip: TERMS.abmeldungenJe1k.tip,
      hinweis: `Grau unter ${anz(sw.abmeldungen)} Abmeldungen.`,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      gruppe: 'Abmeldungen',
      render: zelle(
        (z) =>
          z.abmeldungen_je_1000 === null ? (
            <Strich grund={z.abmeldungen_grund ?? z.ohne_nenner_grund ?? 'Nicht messbar.'} />
          ) : (
            jeTausend(z.abmeldungen_je_1000)
          ),
        (r) => (r.je.abmeldungen_je_1000 === null ? <Strich grund={r.z.abmeldungen_grund ?? 'Nicht messbar.'} /> : jeTausend(r.je.abmeldungen_je_1000)),
      ),
      sortValue: nurFlaeche((z) => z.abmeldungen_je_1000),
      unsicher: unsicherFlaeche((z) =>
        nurMitWert(z.abmeldungen_je_1000, z.abmeldungen !== null && z.abmeldungen < sw.abmeldungen ? wenig.abmeldungen : null),
      ),
    },
    {
      key: 'abmAnzahl',
      label: TERMS.abmeldungenAnzahl.label,
      tip: TERMS.abmeldungenAnzahl.tip,
      einheit: 'Anzahl',
      align: 'right',
      gruppe: 'Abmeldungen',
      render: zelle((z) => (z.abmeldungen === null ? <Strich grund={z.abmeldungen_grund ?? 'Nicht messbar.'} /> : anz(z.abmeldungen))),
      sortValue: nurFlaeche((z) => z.abmeldungen),
    },
    {
      key: 'wertJeAbm',
      label: TERMS.wertJeAbmeldung.label,
      tip: TERMS.wertJeAbmeldung.tip,
      hinweis: `Grau unter ${anz(sw.anfos)} Anfos oder ${anz(sw.abmeldungen)} Abmeldungen.`,
      einheit: 'erwarteter Wert',
      align: 'right',
      gruppe: 'Abmeldungen',
      render: zelle((z) =>
        z.wert_je_abmeldung === null ? <Strich grund={z.abmeldungen_grund ?? 'Keine Abmeldungen über diese Fläche.'} /> : euro1(z.wert_je_abmeldung),
      ),
      sortValue: nurFlaeche((z) => z.wert_je_abmeldung),
      unsicher: unsicherFlaeche((z) =>
        nurMitWert(
          z.wert_je_abmeldung,
          z.anfos_ohne_vorlaeufige < sw.anfos ? wenig.anfos : z.abmeldungen !== null && z.abmeldungen < sw.abmeldungen ? wenig.abmeldungen : null,
        ),
      ),
    },
  ];
}

/** Menü in Gruppenreihenfolge (die Tabelle behält ihre eigene Reihenfolge). */
const menueAus = (spalten: Spalte<FZeile>[]) =>
  spaltenMeta(spalten)
    .map((m) => ({ ...m, label: MENUE[m.key] ?? m.label }))
    .sort((a, b) => GRUPPEN.indexOf(a.gruppe as (typeof GRUPPEN)[number]) - GRUPPEN.indexOf(b.gruppe as (typeof GRUPPEN)[number]));

/** Direktvergleich Vortag/Webinartag auf denselben Webinaren (E11), nur ab `mindestens` gemeinsamen Webinaren. */
const DirektvergleichZeile: FC<{ data: WerbeflaechenAntwort }> = ({ data }) => {
  const d = data.direktvergleich;
  if (!d) {
    if (data.personen_berechnet) return null;
    return (
      <Hinweis>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {TERMS.direktvergleichHeatUps.label}:
        </Box>{' '}
        wird beim nächsten stündlichen Lauf nach dem Update berechnet.
      </Hinweis>
    );
  }
  if (!d.sichtbar) return null;
  const jeWebinar = d.webinare
    .map((w) => `${w.start ? tm(w.start) : w.webinar_id}: Vortag ${jeTausend(w.vortag.teilnehmer_je_1000)} · Webinartag ${jeTausend(w.webinartag.teilnehmer_je_1000)}`)
    .join('; ');
  return (
    <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: '8px', fontSize: 14, lineHeight: 1.6, maxWidth: '90ch' }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {TERMS.direktvergleichHeatUps.label}
      </Box>
      <InfoTip text={`${TERMS.direktvergleichHeatUps.tip} Teilnehmer je 1.000 je Webinar — ${jeWebinar}.`} /> ({anz(d.anzahl)} Webinare mit
      beiden Heat-Ups, ohne Tracking-Warnung): Teilnehmer je 1.000 Kontakte{' '}
      <Box component="span" sx={{ fontWeight: 600 }}>
        Vortag {jeTausend(d.vortag.teilnehmer_je_1000)} · Webinartag {jeTausend(d.webinartag.teilnehmer_je_1000)}
      </Box>
      ; erwarteter Wert je 1.000 {euroJeTausend(d.vortag.wert_je_1000)} · {euroJeTausend(d.webinartag.wert_je_1000)}; Show-up{' '}
      {prozent(d.vortag.show_up)} · {prozent(d.webinartag.show_up)}
      {d.messluecke_webinare
        ? ` (ohne ${anz(d.messluecke_webinare)} ${d.messluecke_webinare === 1 ? 'Webinar' : 'Webinare'} mit Messlücke oder übernommenen Anmeldungen)`
        : ''}
      . Mehr
      Teilnehmer je 1.000 brachte der Vortag in {anz(d.vortag_vorn)} von {anz(d.anzahl)} Webinaren.
      {/* B65 (26.10.2026): Der Hinweis zur Zuordnung stand nur im Tooltip-Chip unter der Karte — Leser schlossen „Webinartag streichen“. */}
      <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
        Die Zuordnung zur ersten Anmeldung bevorzugt den früheren Vortag: Wer sich schon über ihn angemeldet hat, zählt beim Webinartag nicht
        mehr. Ein niedrigerer Wert des Webinartags heißt deshalb nicht, dass er verzichtbar ist. Beruht auf wenigen Webinaren: Ein einzelnes
        Webinar kann die Reihenfolge drehen.
      </Box>
    </Box>
  );
};

/**
 * Hinweis unter der Karte (nur wenn Anmeldungen, Show-up oder Teilnehmer sichtbar sind), je Fenster aus der API.
 * Warn-Webinare UND Messlücken je mit eigenem Satz — bis 26.10.2026 verdrängte der erste den zweiten, bei 6 und 12 Monaten
 * stand die Messlücke deshalb nie da (B59).
 */
function teilnahmeHinweis(data: WerbeflaechenAntwort): string {
  const h = data.hinweis_teilnahme;
  const ab = datumLang(data.nach_beginn_erfasst_ab);
  const saetze: string[] = [];
  if (h.warn_webinare.length > 0) {
    saetze.push(
      `${anz(h.anmeldungen)} Anmeldungen aus ${webinareText(h.warn_webinare.length)} mit Tracking-Warnung (${starts(h.warn_webinare)}) zählen bei Anmeldungen, Show-up und Teilnehmern nicht mit, beim erwarteten Wert schon.`,
    );
  }
  const quoten = h.messluecke_webinare.map((w) => w.show_up).filter((v): v is number => v !== null);
  if (h.messluecke_webinare.length > 0) {
    const min = quoten.length > 0 ? Math.min(...quoten) : null;
    const max = quoten.length > 0 ? Math.max(...quoten) : null;
    const spanne =
      min === null || max === null ? '' : Math.round(min * 100) === Math.round(max * 100) ? ` (Show-up dort ${prozent(min, 0)})` : ` (Show-up dort ${Math.round(min * 100)}–${prozent(max, 0)})`;
    saetze.push(
      `Bei ${webinareText(h.messluecke_webinare.length)} (${starts(h.messluecke_webinare)}) hat das frühere Werkzeug die Teilnahme lückenhaft erfasst, unterhalb der Warnschwelle${spanne}: Beim Show-up zählen sie nicht mit, bei Anmeldungen, Teilnehmern und Anfos schon — die Teilnehmer je 1.000 sind dadurch leicht gedrückt.`,
    );
  }
  const uebernommen = h.uebernommen_webinare ?? [];
  if (uebernommen.length > 0) {
    // Nachtrag Till 26.10.2026: wie P1 („—", Grund „Anmeldungen übernommen“) nicht im Show-up.
    saetze.push(
      `Bei ${webinareText(uebernommen.length)} (${starts(uebernommen)}) wurden die Anmeldungen aus einer anderen Quelle übernommen; ihr Show-up ist nicht vergleichbar: Beim Show-up zählen sie nicht mit, bei Anmeldungen, Teilnehmern und Anfos schon.`,
    );
  }
  saetze.push(`Teilnehmer sind eine Untergrenze, der Show-up ist eher zu niedrig als zu hoch. Anmeldungen nach Beginn werden erst seit ${ab} erfasst.`);
  return saetze.join(' ');
}

const EinladungenKarte: FC<{ data: WerbeflaechenAntwort | null; fehler: string | null }> = ({ data, fehler }) => {
  const block = useMemo(() => (data ? data.rows.filter((z) => z.block === 'anmeldung') : []), [data]);
  const zeilen = useMemo(
    () =>
      zeilenAus(block, (a, b) => (b.trichter?.gesamt.anmeldungen ?? 0) - (a.trichter?.gesamt.anmeldungen ?? 0) || b.anfos - a.anfos),
    [block],
  );
  const vortag = block.find((z) => z.key === 'heatup_vortag')?.trichter ?? null;
  const webinartag = block.find((z) => z.key === 'heatup_webinartag')?.trichter ?? null;
  const sw = data?.grau_schwellen ?? SCHWELLEN_ERSATZ;

  const meta = menueAus(baueSpalten({ data, sw, sichtbar: new Set(), schmal: false, namensBreite: 220, vortag, webinartag }));
  const wahl = useSpaltenwahl('einladungen', meta, EINLADUNGEN_STANDARDS);
  const [breiteRef, breitePx] = useBreitePx(wahl.messRef);
  const sichtbar = new Set(wahl.sichtbar);
  const schmal = wahl.breite === 'schmal';
  // Namensspalte 220 px (vorschlag_v3 §4.3); bei 1.024 px mit offener Seitenleiste (Inhalt 654 px) lief die Tabelle damit
  // 11 px über (gemessen 23.10.) — unter 760 px Inhalt 180 px, am Handy 72 px (min-width zählt ohne Innenabstand).
  const namensBreite = schmal ? 72 : breitePx !== null && breitePx < 760 ? 180 : 220;
  const spalten = baueSpalten({ data, sw, sichtbar, schmal, namensBreite, vortag, webinartag }).filter((s) => sichtbar.has(s.key));
  const trichterSichtbar = ['anm1k', 'showUp', 'teiln1k', 'anmAnzahl', 'teilnAnzahl', 'showUpIndex', 'anfos100Teiln'].some((k) => sichtbar.has(k));
  // Flächen, deren Trichter der neue Schreiber erst nach dem Update füllt (Vertrag §4).
  const ohneTrichter = block.filter((z) => z.kontakte !== null && z.trichter === null).map((z) => z.label);
  const fussnote2 = block.some((z) => z.trichter?.anmeldungen_alle_webinare);

  return (
    <ChartCard
      title="Webinar-Einladungen: vom Kontakt zur Anfo"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead={
        <>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Vergleiche Teilnehmer und erwarteten Wert je 1.000 Kontakte, nicht nur die Anmeldungen.
          </Box>{' '}
          Eine Einladung mit weniger Anmeldungen kann mehr Teilnehmer bringen, wenn mehr Angemeldete live dabei sind. Eine Person zählt bei der
          Einladung, über die sie sich zuerst zum Webinar angemeldet hat, mit ihrer Teilnahme und mit ihrer ersten Anfo zu diesem Webinar, egal
          über welchen Link sie bestellt hat.
        </>
      }
      terms={[
        { label: ATTRIBUTION.ersteAnmeldung.label, tip: ATTRIBUTION.ersteAnmeldung.tip },
        { label: TERMS.werbeflaeche.label, tip: TERMS.werbeflaeche.tip },
        { label: TERMS.kontakteZustellungen.label, tip: TERMS.kontakteZustellungen.tip },
        { label: TERMS.anmeldungenJe1k.label, tip: TERMS.anmeldungenJe1k.tip },
        { label: TERMS.showUp.label, tip: TERMS.showUp.tip },
        { label: TERMS.teilnehmerJe1k.label, tip: TERMS.teilnehmerJe1k.tip },
        { label: TERMS.wertJe1kFlaeche.label, tip: TERMS.wertJe1kFlaeche.tip },
        { label: TERMS.abmeldungenJe1k.label, tip: TERMS.abmeldungenJe1k.tip },
        {
          label: TERMS.wenigDaten.label,
          tip: `${TERMS.wenigDaten.tip} Grau unter ${anz(sw.anfos)} Anfos (Wert und Anfos je 1.000, Anfos je 100), unter ${anz(sw.anmeldungen)} Anmeldungen (Anmeldungen je 1.000), unter ${anz(sw.rechtzeitig)} vor Beginn Angemeldeten (Show-up, Teilnehmer je 1.000, Show-up-Index) und unter ${anz(sw.abmeldungen)} Abmeldungen.`,
        },
        ...(data?.direktvergleich?.sichtbar ? [{ label: TERMS.direktvergleichHeatUps.label, tip: TERMS.direktvergleichHeatUps.tip }] : []),
        ...(block.some((z) => z.provisional > 0) ? [{ label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip }] : []),
      ]}
      source="Webinar-Versände, Newsletter-Ausgaben, Versand-Bücher der Flächen, Webinar-Anmeldungen und Teilnahme, PL-X-Bestellungen"
      freshnessKey={['webinar_send_metrics', 'newsletter_ausgaben', 'plx_orders', 'anfo_werte']}
    >
      <Box ref={breiteRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>{kartenkopf(data, block)}</Box>
            {ohneTrichter.length > 0 && trichterSichtbar && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Für {ohneTrichter.join(', ').replace(/, ([^,]*)$/, ' und $1')} stehen Anmeldungen, Show-up und Teilnehmer noch nicht bereit — sie
                werden beim nächsten stündlichen Lauf nach dem Update berechnet. Bis dahin steht dort „—“; Kontakte, erwarteter Wert und
                Abmeldungen stimmen.
              </Alert>
            )}
            <SortableTable
              rows={zeilen}
              columns={spalten}
              rowKey={(r) => r.id}
              initialSort={{ key: 'wert1k', dir: 'desc' }}
              kinder={(r): FZeile[] | undefined => {
                if (r.art === 'sammel') return r.kinder;
                if (r.art === 'flaeche' && r.z.je_zustellung) return [{ art: 'zustellung', id: `${r.id}/je`, z: r.z, je: r.z.je_zustellung }];
                return undefined;
              }}
              kinderImmerOffen={(r) => r.art === 'flaeche'}
              abschnitt={(r) => (r.art === 'sammel' ? '' : r.z.beendet_am ? 'Nicht mehr im Einsatz' : null)}
              // Unter 1.000 px Inhalt eng: mit Kontakte als Zusatzspalte liefen 7 Spalten bei 1.280 px mit offener
              // Seitenleiste (910 px) sonst 25 px über (gemessen 23.10.).
              eng={schmal || (breitePx !== null && breitePx < 1000)}
              festeErsteSpalte
            />
            <DirektvergleichZeile data={data} />
            {trichterSichtbar && (
              <Hinweis>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Teilnahmedaten:
                </Box>{' '}
                {teilnahmeHinweis(data)}
              </Hinweis>
            )}
            <Hinweis>
              ¹ Ohne Webinare mit Tracking-Warnung (unvollständige Teilnahmedaten). Show-up und Show-up-Index lassen zusätzlich die
              Webinare mit bekannter Messlücke im früheren Werkzeug und die mit übernommenen Anmeldungen weg; bei Anmeldungen und
              Teilnehmern zählen sie mit.
              {fussnote2 && ' ² Kontakte nicht nach Webinar trennbar, deshalb alle Webinare.'} Euro netto. — = nicht messbar; der Grund steht im
              Tooltip. Bei Flächen mit Kontaktzahl zählen die Anfos zum Versand, in „Woher die Anfos kommen“ zum Bestelltag. Newsletter-Plätze
              haben Ausgabendaten erst ab {datumLang(data.newsletter_daten_ab)}. Die Seite „Webinar Auswertungen“ (Reiter Quellen-Effizienz)
              zählt anders: brutto, alle Anmeldungen, nur gewählte Marken, ohne die neuen Werbeflächen. Bei Abweichungen gilt diese Karte.
              {vorlaeufigHinweis(block)}
              {data.stichtag_hinweis ? ` ${data.stichtag_hinweis}` : ''}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default EinladungenKarte;
